import { useState, useCallback, useEffect, useRef } from 'react';
import {
  GitBranch,
  Loader2,
  Plus,
  X,
  RotateCcw,
  Sparkles,
  Play,
  Save,
  FolderOpen,
  SlidersHorizontal,
  Workflow as WorkflowIcon,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Separator } from '@easyflow/ui-frontend/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-frontend/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@easyflow/ui-frontend/components/ui/alert-dialog';
import { usePermissions } from '@/contexts/PermissionContext';
import { usePendingNavigation } from '@/hooks/usePendingNavigation';
import { apiClient as api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { EmbeddedWorkflowBuilder } from '@/components/workflow/EmbeddedWorkflowBuilder';
import {
  WorkflowsListDialog,
  type WorkflowsListDialogItem,
} from '@/components/workflow/WorkflowsListDialog';
import { NewWorkflowDialog, type NewWorkflowDraft } from '@/components/workflow/NewWorkflowDialog';
import {
  WorkflowsManageDialog,
  type WorkflowManageItem,
} from '@/components/workflow/WorkflowsManageDialog';
import { runStatusBadge, fmtDuration } from '@/components/workflow/workflow-shared';
import type { WorkflowNode } from '@/pages/crm/shared/types';
import type {
  EmbeddedWorkflowBuilderRef,
  WorkflowState,
  InitialDraft,
} from '@/components/workflow/EmbeddedWorkflowBuilder';
import type { WorkflowListItem, WorkflowRunDetail, WorkflowEditorDefinition } from '@easyflow/shared';

const TERMINAL_RUN_STATUSES = new Set(['completed', 'failed', 'cancelled']);

function runDurationMs(run: WorkflowRunDetail): number | null {
  if (!run.started_at || !run.completed_at) return null;
  const start = new Date(run.started_at).getTime();
  const end = new Date(run.completed_at).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  return Math.max(0, end - start);
}

const EMPTY_STATE: WorkflowState = {
  workflowId: null,
  name: '',
  description: '',
  version: 1,
  isActive: false,
  isNew: true,
  saving: false,
  running: false,
  nodeCount: 0,
  edgeCount: 0,
  isDirty: false,
};

export default function WorkflowBuilder() {
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canCreateDefinition = !permissionsLoading && hasPermission('settings.manage');
  const { toast } = useToast();
  const builderRef = useRef<EmbeddedWorkflowBuilderRef>(null);
  const pendingNavigateRef = useRef<(() => void) | null>(null);
  const pendingOpenAIRef = useRef<string | null>(null);

  const [workflows, setWorkflows] = useState<WorkflowListItem[]>([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [workflowsError, setWorkflowsError] = useState<string | null>(null);
  const [runDefinition, setRunDefinition] = useState<WorkflowEditorDefinition | null>(null);
  const activating = useRef(new Set<string>());

  // Gate state: editingWorkflowId=null AND no initialDraft → gate
  const [editingWorkflowId, setEditingWorkflowId] = useState<string | null>(() => new URLSearchParams(window.location.search).get('workflow'));
  const [initialDraft, setInitialDraft] = useState<InitialDraft | null>(null);

  const [showWorkflowsList, setShowWorkflowsList] = useState(false);
  const [showNewWorkflow, setShowNewWorkflow] = useState(false);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [activatingIds, setActivatingIds] = useState<Set<string>>(new Set());
  const [activationConfirm, setActivationConfirm] = useState<{ id: string; active: boolean } | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteWorkflowId, setDeleteWorkflowId] = useState<string | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateWorkflowId, setDuplicateWorkflowId] = useState<string | null>(null);
  const [duplicateWorkflowName, setDuplicateWorkflowName] = useState('');

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<WorkflowRunDetail | null>(null);
  const [runResultOpen, setRunResultOpen] = useState(false);

  const [workflowState, setWorkflowState] = useState<WorkflowState>(EMPTY_STATE);
  const navigationGuard = usePendingNavigation(workflowState.isDirty || (workflowState.isNew && workflowState.nodeCount > 0));

  const isInGate = editingWorkflowId === null && initialDraft === null;

  const loadWorkflows = useCallback(async () => {
    try {
      setWorkflowsLoading(true);
      const all: WorkflowListItem[] = [];
      let offset = 0;
      for (;;) {
        const page = await api.getWorkflowsList({ limit: 200, offset });
        all.push(...page.workflows);
        offset += page.workflows.length;
        if (offset >= Number(page.total)) break;
        if (!page.workflows.length) throw new Error('Catalogo cambiato durante la lettura: riprova');
      }
      setWorkflows([...new Map(all.map(item => [item.id, item])).values()]);
      setWorkflowsError(null);
    } catch (error) {
      console.error('Errore nel caricamento dei workflow:', error);
      setWorkflowsError('Catalogo non aggiornato: riprova il caricamento.');
    } finally {
      setWorkflowsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  const activateWorkflow = async (id: string, nextActive: boolean) => {
    if (activating.current.has(id) || !workflows.find(w => w.id === id)?.capabilities.can_toggle_global) return;
    activating.current.add(id);
    setActivatingIds(new Set(activating.current));
    try {
      await api.post(`/workflows/${id}/activate`, { is_active: nextActive });
      await loadWorkflows();
      toast({ title: nextActive ? 'Workflow attivato' : 'Workflow disattivato' });
    } catch (error) {
      await loadWorkflows();
      toast({ title: 'Errore', description: 'Impossibile aggiornare lo stato', variant: 'destructive' });
    } finally {
      activating.current.delete(id);
      setActivatingIds(new Set(activating.current));
    }
  };

  const deleteWorkflow = async (id: string) => {
    if (!workflows.find(w => w.id === id)?.capabilities.can_delete_definition) return;
    try {
      await api.delete(`/workflows/${id}`);
      setWorkflows((prev) => prev.filter((w) => w.id !== id));
      if (editingWorkflowId === id) {
        setEditingWorkflowId(null);
        setInitialDraft(null);
      }
      toast({ title: 'Workflow eliminato' });
      setDeleteDialogOpen(false);
      setDeleteWorkflowId(null);
    } catch (error) {
      toast({ title: 'Errore', description: 'Impossibile eliminare', variant: 'destructive' });
    }
  };

  const cloneWorkflow = (id: string, newName: string) => {
    requestNavigateAway(() => { void performClone(id, newName); });
  };
  const performClone = async (id: string, newName: string) => {
    if (!workflows.find(w => w.id === id)?.capabilities.can_clone_editable) return;
    try {
      const clonedWorkflow = await api.cloneWorkflowDefinition(id, newName);
      if (clonedWorkflow?.id) {
        await loadWorkflows();
        toast({ title: 'Workflow duplicato', description: `"${newName}" creato con successo` });
        setDuplicateDialogOpen(false);
        setDuplicateWorkflowId(null);
        setDuplicateWorkflowName('');
        setInitialDraft(null); setEditingWorkflowId(clonedWorkflow.id);
      }
    } catch (error) {
      toast({ title: 'Errore', description: 'Impossibile duplicare il workflow', variant: 'destructive' });
    }
  };

  const handleResetConfirm = () => {
    builderRef.current?.resetCanvas();
    setResetDialogOpen(false);
    toast({ title: 'Canvas resettato' });
  };

  const isWorkflowDirty = () =>
    workflowState.isDirty || (workflowState.isNew && workflowState.nodeCount > 0);

  const requestNavigateAway = (action: () => void) => {
    if (isWorkflowDirty()) {
      pendingNavigateRef.current = action;
      setCloseDialogOpen(true);
    } else {
      action();
    }
  };

  const handleSelectWorkflow = (id: string) => {
    requestNavigateAway(() => {
      pendingOpenAIRef.current = null;
      setInitialDraft(null);
      setWorkflowState(EMPTY_STATE);
      setEditingWorkflowId(id);
      setShowWorkflowsList(false);
    });
  };

  const handleEditWithAI = (id: string) => {
    requestNavigateAway(() => {
      pendingOpenAIRef.current = id;
      setInitialDraft(null);
      setWorkflowState(EMPTY_STATE);
      setEditingWorkflowId(id);
      setShowWorkflowsList(false);
    });
  };

  // Apre il modale AI Builder solo dopo che il workflow target è caricato per davvero:
  //  - editingWorkflowId del parent === id richiesto
  //  - workflowState appartiene allo stesso workflow (workflowId match) → no race su switch A→B
  //  - non flaggato come nuovo
  //  - nome popolato
  //  - almeno un nodo nel canvas
  // Senza l'attesa sui nodi l'AI riceveva un context con `nodes: []` e ragionava
  // come se stesse partendo da zero invece di modificare il template aperto.
  // Safety net: se dopo 2s i nodi non arrivano (workflow legittimamente vuoto)
  // apriamo comunque, così l'AI vede almeno nome/descrizione/trigger.
  useEffect(() => {
    const targetId = pendingOpenAIRef.current;
    if (!targetId) return;
    if (editingWorkflowId !== targetId) return;
    if (workflowState.workflowId !== targetId) return;
    if (workflowState.isNew) return;
    if (!workflowState.name) return;

    const hasStructure = workflowState.nodeCount > 0;

    if (hasStructure) {
      pendingOpenAIRef.current = null;
      const t = setTimeout(() => builderRef.current?.openAIBuilder(), 200);
      return () => clearTimeout(t);
    }

    // Aspetta i nodi, ma non per sempre.
    const safety = setTimeout(() => {
      if (pendingOpenAIRef.current === targetId) {
        pendingOpenAIRef.current = null;
        builderRef.current?.openAIBuilder();
      }
    }, 2000);
    return () => clearTimeout(safety);
  }, [editingWorkflowId, workflowState.workflowId, workflowState.isNew, workflowState.name, workflowState.nodeCount]);

  const handleNewWorkflowConfirm = (draft: NewWorkflowDraft) => {
    requestNavigateAway(() => {
      pendingOpenAIRef.current = null;
      setEditingWorkflowId(null);
      setInitialDraft(draft);
      setShowNewWorkflow(false);
      setShowWorkflowsList(false);
    });
  };

  const handleRunFromList = async (id: string) => {
    try { setRunDefinition(await api.getWorkflowDefinition(id)); }
    catch { toast({ title: 'Definizione non disponibile', description: 'Riprova il caricamento prima di eseguire.', variant: 'destructive' }); }
  };
  const executeFromList = async () => {
    if (!runDefinition || runDefinition.admission_scope !== 'tenant' || runningId) return;
    const id = runDefinition.id;
    setRunningId(id);
    try {
      const start = await api.runSavedWorkflow(id);
      const runId = start.run_id;
      setRunDefinition(null);

      const deadline = Date.now() + 30000;
      let detail: WorkflowRunDetail | null = null;
      while (Date.now() < deadline) {
        const current = await api.getWorkflowRun(runId);
        if (current && TERMINAL_RUN_STATUSES.has(current.status)) {
          detail = current;
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }

      if (detail) {
        setRunResult(detail);
        setRunResultOpen(true);
        loadWorkflows();
      } else {
        toast({
          title: 'Esecuzione in corso',
          description: `Il workflow è ancora in esecuzione (run ${runId.slice(0, 8)}…). Consulta la cronologia in "Gestisci".`,
        });
      }
    } catch (error) {
      toast({ title: 'Errore', description: 'Impossibile avviare il workflow', variant: 'destructive' });
    } finally {
      setRunningId(null);
    }
  };

  const requestCloseWorkflow = () => {
    requestNavigateAway(() => {
      pendingOpenAIRef.current = null;
      setEditingWorkflowId(null);
      setInitialDraft(null);
    });
  };

  const doCloseWorkflow = () => {
    const action = pendingNavigateRef.current;
    pendingNavigateRef.current = null;
    setCloseDialogOpen(false);
    action?.();
  };

  const cancelCloseWorkflow = () => {
    pendingNavigateRef.current = null;
    setCloseDialogOpen(false);
  };

  const dialogItems: WorkflowsListDialogItem[] = workflows;

  const manageItems: WorkflowManageItem[] = workflows;

  const headerDisabled = isInGate || !!workflowState.unavailable;

  return (
    <div className="flex flex-col h-full">
      {navigationGuard}
      {workflowsError && <div role="alert" className="p-2 text-sm">{workflowsError} <Button size="sm" variant="outline" onClick={loadWorkflows}>Riprova</Button></div>}
      {new URLSearchParams(window.location.search).get('from') === 'campaign' && <p className="text-sm p-2 border-b">La campagna e la bozza sono rimaste nella scheda di origine. Torna a quella scheda dopo la consultazione: nessun collegamento viene creato automaticamente.</p>}
      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-background/95 backdrop-blur shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <GitBranch className="h-5 w-5 text-purple-600 shrink-0" />
          {isInGate ? (
            <h1 className="text-lg font-semibold">Workflow Builder</h1>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="text-base font-semibold truncate" title={workflowState.description || workflowState.name}>
                {workflowState.name || 'Workflow senza nome'}
              </h1>
              {!workflowState.isNew && (
                <Badge variant="secondary" className="text-[10px] px-1.5">
                  v{workflowState.version}
                </Badge>
              )}
              {workflowState.isNew && (
                <Badge variant="outline" className="text-[10px] px-1.5">
                  Bozza
                </Badge>
              )}
              {workflowState.isActive ? (
                <Badge className="text-[10px] px-1.5 bg-green-500/20 text-green-700 dark:text-green-400 border border-green-500/30">
                  Definizione attiva
                </Badge>
              ) : !workflowState.isNew ? (
                <Badge variant="outline" className="text-[10px] px-1.5 text-muted-foreground">
                  Definizione disabilitata
                </Badge>
              ) : null}
              {workflowState.isDirty && (
                <Badge variant="outline" className="text-[10px] px-1.5 border-amber-400 text-amber-600 dark:text-amber-400">
                  Modifiche non salvate
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 ml-1 gap-1.5 text-xs"
                onClick={requestCloseWorkflow}
                title="Torna alla lista workflow"
              >
                <X className="h-3.5 w-3.5" />
                Cambia workflow
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setResetDialogOpen(true)}
            disabled={headerDisabled || workflowState.readOnly || workflowState.nodeCount === 0}
            className="flex items-center gap-1.5 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950 disabled:opacity-50"
            title="Resetta canvas"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
          <Button
            size="sm"
            onClick={() => builderRef.current?.openAIBuilder()}
            disabled={headerDisabled || workflowState.readOnly}
            className="flex items-center gap-1.5 bg-violet-500 hover:bg-violet-600 text-white border-l-2 border-l-violet-300 disabled:opacity-50"
            title="AI Workflow Builder"
          >
            <Sparkles className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            onClick={() => builderRef.current?.run()}
            disabled={headerDisabled || workflowState.isNew || workflowState.running}
            className={`flex items-center gap-1.5 ${
              headerDisabled || workflowState.isNew ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
            }`}
            title="Esegui la versione salvata"
            aria-label="Esegui la versione salvata"
          >
            {workflowState.running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          </Button>
          <Button
            size="sm"
            onClick={() => builderRef.current?.save()}
            disabled={headerDisabled || workflowState.readOnly || workflowState.saving}
            aria-label="Salva workflow"
            title="Salva workflow"
            className="disabled:opacity-50"
          >
            {workflowState.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          </Button>

          <Separator orientation="vertical" className="h-6 mx-1" />

          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowWorkflowsList(true)}
            className="flex items-center gap-2"
          >
            <FolderOpen className="h-4 w-4" />
            Apri workflow
          </Button>
          <Button
            size="sm"
            onClick={() => setShowNewWorkflow(true)}
            disabled={!canCreateDefinition}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Nuovo
          </Button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 relative">
        {isInGate ? (
          <GateScreen
            workflowsCount={workflows.length}
            onOpenList={() => setShowWorkflowsList(true)}
            onNew={() => setShowNewWorkflow(true)}
            onManage={() => setShowManageDialog(true)}
          />
        ) : (
          <div className="flex-1 min-h-0">
            <EmbeddedWorkflowBuilder
              ref={builderRef}
              key={editingWorkflowId || (initialDraft ? `draft-${initialDraft.name}` : 'new')}
              workflowId={editingWorkflowId}
              initialDraft={initialDraft}
              onSaved={(saved) => {
                loadWorkflows();
              }}
              onRequestOpenSelector={() => setShowWorkflowsList(true)}
              externalToolbar={true}
              onWorkflowStateChange={setWorkflowState}
            />
          </div>
        )}
      </div>

      <Dialog open={!!runDefinition} onOpenChange={open => { if (!open) setRunDefinition(null); }}>
        <DialogContent><DialogHeader><DialogTitle>Esegui la versione salvata</DialogTitle>
          <DialogDescription>“{runDefinition?.name}” v{runDefinition?.version}. Esecuzione reale con possibili effetti esterni, non una simulazione. Nessuna modifica in bozza viene eseguita.</DialogDescription></DialogHeader>
          {runDefinition?.admission_scope !== 'tenant' && <p role="alert">Avvio impedito: manca un contesto campagna verificabile oppure l’ambito non è confermato.</p>}
          <DialogFooter><Button variant="outline" onClick={() => setRunDefinition(null)}>Annulla</Button><Button disabled={!!runningId || runDefinition?.admission_scope !== 'tenant'} onClick={executeFromList}>Conferma esecuzione reale</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <WorkflowsListDialog
        canCreate={canCreateDefinition}
        open={showWorkflowsList}
        onOpenChange={setShowWorkflowsList}
        workflows={dialogItems}
        loading={workflowsLoading}
        runningId={runningId}
        onSelect={handleSelectWorkflow}
        onNew={() => {
          setShowWorkflowsList(false);
          setShowNewWorkflow(true);
        }}
        onRun={handleRunFromList}
        onDuplicate={(id, name) => {
          setDuplicateWorkflowId(id);
          setDuplicateWorkflowName(name);
          setDuplicateDialogOpen(true);
        }}
        onDelete={(id) => {
          setDeleteWorkflowId(id);
          setDeleteDialogOpen(true);
        }}
        onEditWithAI={handleEditWithAI}
      />

      <WorkflowsManageDialog
        canCreate={canCreateDefinition}
        open={showManageDialog}
        onOpenChange={setShowManageDialog}
        workflows={manageItems}
        loading={workflowsLoading}
        togglingIds={activatingIds}
        onActivate={(id, active) => setActivationConfirm({ id, active })}
        onCreate={() => {
          setShowManageDialog(false);
          setShowNewWorkflow(true);
        }}
      />

      <Dialog open={!!activationConfirm} onOpenChange={open => { if (!open) setActivationConfirm(null); }}>
        <DialogContent><DialogHeader><DialogTitle>Modifica abilitazione generale</DialogTitle>
          <DialogDescription>Questa operazione modifica la definizione condivisa e riguarda tutte le campagne collegate, non soltanto quella di provenienza. Non ripristina regole sostituite e non annulla il lavoro già ammesso.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setActivationConfirm(null)}>Annulla</Button>
            <Button onClick={() => { if (activationConfirm) { void activateWorkflow(activationConfirm.id, activationConfirm.active); setActivationConfirm(null); } }}>Conferma modifica generale</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <NewWorkflowDialog
        open={showNewWorkflow && canCreateDefinition}
        onOpenChange={setShowNewWorkflow}
        onConfirm={handleNewWorkflowConfirm}
      />

      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resetta Canvas</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler resettare il canvas? Tutti i nodi e le connessioni verranno rimossi. Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Resetta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={closeDialogOpen}
        onOpenChange={(open) => {
          if (!open) cancelCloseWorkflow();
          else setCloseDialogOpen(true);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Modifiche non salvate</AlertDialogTitle>
            <AlertDialogDescription>
              Hai modifiche non salvate sul workflow corrente. Se prosegui le perderai. Vuoi continuare?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelCloseWorkflow}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={doCloseWorkflow}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Procedi senza salvare
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questo workflow? Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteWorkflowId && deleteWorkflow(deleteWorkflowId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplica Workflow</DialogTitle>
            <DialogDescription>
              Inserisci un nome per il nuovo workflow duplicato.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="duplicate-name">Nome workflow</Label>
            <Input
              id="duplicate-name"
              value={duplicateWorkflowName}
              onChange={(e) => setDuplicateWorkflowName(e.target.value)}
              placeholder="Nome del nuovo workflow"
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={() => duplicateWorkflowId && cloneWorkflow(duplicateWorkflowId, duplicateWorkflowName)}
              disabled={!duplicateWorkflowName.trim()}
            >
              Duplica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={runResultOpen} onOpenChange={setRunResultOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Esito esecuzione
              {runResult && runStatusBadge(runResult.status)}
            </DialogTitle>
            <DialogDescription>{runResult?.workflow_name || 'Workflow'}</DialogDescription>
          </DialogHeader>
          {runResult && (
            <div className="space-y-3 py-2 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Durata</span>
                <span className="font-medium">{fmtDuration(runDurationMs(runResult)) || '—'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Run ID</span>
                <span className="font-mono text-xs break-all text-right">{runResult.id}</span>
              </div>
              {runResult.status === 'failed' && runResult.error_message && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-destructive">
                  <p className="font-medium mb-1">Errore</p>
                  <p className="text-xs break-words">{runResult.error_message}</p>
                </div>
              )}
              {runResult.status === 'completed' && (
                <div className="rounded-md border border-success/30 bg-success/10 p-3 text-xs text-success">
                  Esecuzione completata senza errori.
                </div>
              )}
              {runResult.status === 'cancelled' && (
                <div className="rounded-md border bg-muted p-3 text-xs text-muted-foreground">
                  Esecuzione annullata.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRunResultOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface GateScreenProps {
  workflowsCount: number;
  onOpenList: () => void;
  onNew: () => void;
  onManage: () => void;
}

function GateScreen({ workflowsCount, onOpenList, onNew, onManage }: GateScreenProps) {
  return (
    <div className="flex-1 flex items-center justify-center px-6 py-10 bg-gradient-to-b from-background via-background to-muted/30">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-10">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-500/10 mb-4">
            <WorkflowIcon className="h-7 w-7 text-purple-600" />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight">Workflow Builder</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Apri un workflow esistente o creane uno nuovo per iniziare a lavorare sul canvas.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <button
            type="button"
            onClick={onOpenList}
            className="group rounded-2xl border bg-card p-6 text-left transition-all hover:border-purple-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-400"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-purple-500/10 mb-4 group-hover:bg-purple-500/15 transition-colors">
              <FolderOpen className="h-5 w-5 text-purple-600" />
            </div>
            <h3 className="text-base font-semibold mb-1">Apri esistente</h3>
            <p className="text-sm text-muted-foreground">
              Scegli un workflow dalla lista{workflowsCount > 0 ? ` (${workflowsCount} disponibili)` : ''}.
            </p>
          </button>

          <button
            type="button"
            onClick={onNew}
            className="group rounded-2xl border bg-card p-6 text-left transition-all hover:border-emerald-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-400"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 mb-4 group-hover:bg-emerald-500/15 transition-colors">
              <Plus className="h-5 w-5 text-emerald-600" />
            </div>
            <h3 className="text-base font-semibold mb-1">Nuovo workflow</h3>
            <p className="text-sm text-muted-foreground">
              Parti da zero impostando nome, descrizione e trigger.
            </p>
          </button>

          <button
            type="button"
            onClick={onManage}
            className="group rounded-2xl border bg-card p-6 text-left transition-all hover:border-amber-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/10 mb-4 group-hover:bg-amber-500/15 transition-colors">
              <SlidersHorizontal className="h-5 w-5 text-amber-600" />
            </div>
            <h3 className="text-base font-semibold mb-1">Gestisci workflow</h3>
            <p className="text-sm text-muted-foreground">
              Accendi o spegni i workflow e scopri cosa fanno, con la cronologia delle esecuzioni.
            </p>
          </button>
        </div>

        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={onNew}
            className="text-sm text-violet-600 hover:text-violet-700 inline-flex items-center gap-1.5 hover:underline"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Crea con AI
          </button>
        </div>
      </div>
    </div>
  );
}
